import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaPlusSquare, FaTimesCircle } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPaymentMethods } from '../../store/reducers/paymentReducer'
import { generateBid } from '../../store/reducers/propertiesSlice'
import {
  checkOnboarding,
  generateOnboardingLink
} from '../../store/reducers/onboardingReducer'
import type { RootState, AppDispatch } from '../../store/store'
import { fetchUserProfile } from '../../store/reducers/authReducer'; 

import './SellReview.css'
import { ENDPOINTS } from '../../utilities/endpoints'

interface LocationState {
  buildingName:  string
  price:         number
  salesTax:      number
  processingFee: number
  total:         number
  propertyId:    string
  lat:           number
  lng:           number
}

export default function SellReview() {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { state } = useLocation()
  const data = state as LocationState

  const [showConfirm, setShowConfirm] = useState(false)
  const [isSubmittingAsk, setIsSubmittingAsk] = useState(false)
useEffect(() => {
  dispatch(fetchUserProfile())
}, [dispatch])

const { profile, isFetchingProfile } = useSelector((s: RootState) => s.auth)

const isStripeIntegrated = profile?.isStripeIntegrated
  const {
    loading: onboardingLoading,
    error: onboardingError
  } = useSelector((s: RootState) => s.onboarding)

  const {
    cardList,
    isFetching: cardsLoading,
    fetchError: cardsError,
    isBuying,
    buyError,
    buySuccessMsg
  } = useSelector((s: RootState) => s.payment)
  const defaultCard = cardList.find(c => c.isDefault)

  useEffect(() => {
    if (!data || !data.buildingName) {
      navigate(-1)
    }
  }, [data, navigate])

  useEffect(() => {
    dispatch(checkOnboarding())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchPaymentMethods())
  }, [dispatch])

  useEffect(() => {
    if (buySuccessMsg) {
      alert(buySuccessMsg)
      navigate(-1)
    }
  }, [buySuccessMsg, navigate])

  useEffect(() => {
    if (buyError) {
      alert(`Purchase failed: ${buyError}`)
    }
  }, [buyError])

  if (!data || !data.buildingName) return null

  const handleContinue = () => {
    if (!defaultCard) {
      alert('Please add a card first.')
      return
    }
    setShowConfirm(true)
  }

  const handleSell = async () => {  
    if (isFetchingProfile) {
      alert('Checking your Stripe onboarding status…')
      return
    }
    if (!isStripeIntegrated) {
      const go = window.confirm(
        'You need to complete your Stripe onboarding before placing an ask.\n\n' +
        'Press OK to start onboarding now, or Cancel to stay here.'
      )
      if (go) {
        const userId = localStorage.getItem('userId')!
        const reauthUrl = ENDPOINTS.STRIPE.REAUTH(userId)
        window.location.href = reauthUrl
      }
      return
    }
    setShowConfirm(false)
    setIsSubmittingAsk(true)
    try {
      const updatedProperty = await dispatch(
        generateBid({
          propertyId: data.propertyId,
          bidAmount:  data.price
        })
      ).unwrap()
  
      navigate(-1)
    } catch (err: any) {
      alert('Failed to place ask: ' + err)
    } finally {
      setIsSubmittingAsk(false)
    }
  }
  return (
    <>
      <div className="order-container">
        <div className="order-header">
          <FaArrowLeft
            className="order-back-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="order-screen-title">Place Ask</h2>
          <button
            className="header-add-card-btn"
            onClick={() => navigate('/Add-Card')}
          >
            <FaPlusSquare className="add-icon" />
            Add Card
          </button>
        </div>

        <h1 className="order-title">{data.buildingName}</h1>

        <div className="order-section">
          <div className="order-row">
            <span className="label">Price</span>
            <span className="value">${data.price.toFixed(2)}</span>
          </div>
          <div className="order-row">
            <span className="label">Processing Fee</span>
            <span className="value">${data.processingFee.toFixed(2)}</span>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section total-section">
          <p className="order-note">
            *All applicable taxes are included in price.
          </p>
          <div className="order-row">
            <strong className="label">Total (incl. taxes)*</strong>
            <strong className="value">${data.total.toFixed(2)}</strong>
          </div>
        </div>

        <div className="divider" />

        <div className="order-section">
          {cardsLoading ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">Loading…</span>
            </div>
          ) : cardsError ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value error">{cardsError}</span>
            </div>
          ) : defaultCard ? (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">
                {defaultCard.type} •••• {defaultCard.last4}
              </span>
            </div>
          ) : (
            <div className="order-row">
              <span className="label">Payment Method</span>
              <span className="value">No card on file</span>
            </div>
          )}
        </div>

        <div className="divider" />

        <button
          className="cancel-sale-btn"
          onClick={() => navigate(-1)}
        >
          <FaTimesCircle className="cancel-icon" />
          Cancel Sale
        </button>
        <button
          className="continue-btn"
          onClick={handleContinue}
          disabled={isSubmittingAsk || isBuying}
        >
          {isSubmittingAsk || isBuying ? 'Processing…' : 'Confirm Ask'}
        </button>
      </div>

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Sell</h3>
            <p>
              Are you sure you want to start bids for{' '}
              <strong>{data.buildingName}</strong> at{' '}
              <strong>${data.total.toFixed(2)}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="modal-btn cancel"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="modal-btn buy"
                onClick={handleSell}
                disabled={isSubmittingAsk || onboardingLoading}
              >
                {isSubmittingAsk ? 'Starting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
